import * as THREE from 'three';
import { CSS2DObject, CSS2DRenderer } from 'three/addons/renderers/CSS2DRenderer.js';
import { createBin3D, bin3DPickables, disposeBin3D } from './props/Bin3D';
import { BurstParticles } from './props/BurstParticles';
import { BINS, BIN_ORDER, type BinId } from '../data/bins';
import type { WasteCard } from '../data/cards';

// z is negative (farther from the camera, same side as the houses) so the row projects into
// the screen area *above* the answer-card bottom sheet — verified empirically via camera.project().
// x is 0 (not 0.5) to line the row up with CameraRig's orbit centre, which is x=0: at 0.5 the
// whole row sat ~35 px right of screen centre, and on a tall phone (414×896) that pushed the
// sixth bin — 특수배출함, the red one — half off the right edge, label and mesh both.
const ROW_CENTER = new THREE.Vector3(0, 0, -2);
const ROW_SPACING = 0.95;
// Well above and slightly behind the bin row (y=1.8 vs the labels' ~y=0.95 anchor) — the item
// used to float almost at label height and visually covered the bin names underneath it.
const ITEM_REST_POSITION = new THREE.Vector3(0, 1.8, -2.5);
const IDLE_BOB_SPEED = 1.8;
const IDLE_BOB_AMOUNT = 0.06;
const FLY_DURATION = 0.4;
// Pop up, hold, fade — timed to finish a beat before Game.ts's TRANSFORM_CAPTION_MS reveal delay.
const REVEAL_RISE_END = 0.3;
const REVEAL_HOLD_END = 1.1;
const REVEAL_DURATION = 1.5;
const REVEAL_PEAK_SCALE = 1.35;

// Bin labels are CSS2D elements sized in CSS pixels, but the gap between bins is a *3D*
// projection. The camera has a fixed vertical FOV (Game.ts), so that gap scales with the
// viewport HEIGHT while the label width does not: short phones (320×568) squeezed the six
// labels into ~43 px slots and "일반쓰레기"/"특수배출함" overlapped into mush, while tall ones
// (414×896) spread the row wider than the screen and pushed the last label off the right edge.
// A media query can't see that — it only knows the width — so the label box is measured and
// fitted to the projected gap every frame instead.
const LABEL_MAX_WIDTH_PX = 56;
const LABEL_MIN_WIDTH_PX = 34;
const LABEL_MIN_GAP_PX = 3;
const LABEL_EDGE_MARGIN_PX = 4;
const LABEL_MAX_FONT_REM = 0.58;
const LABEL_MIN_FONT_REM = 0.46;

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

type ItemState = 'idle' | 'flying' | 'hidden';

/** Exported so tests can build the same bin layout without constructing a full SortingStation. */
export function binRowPositions(): Map<BinId, THREE.Vector3> {
  const positions = new Map<BinId, THREE.Vector3>();
  BIN_ORDER.forEach((binId, index) => {
    const x = ROW_CENTER.x + (index - (BIN_ORDER.length - 1) / 2) * ROW_SPACING;
    positions.set(binId, new THREE.Vector3(x, ROW_CENTER.y, ROW_CENTER.z));
  });
  return positions;
}

/**
 * The interactive foreground mechanic: a row of tappable 3D bins plus a floating item sprite.
 * Lives alongside Village (not inside it) so Village stays untouched by this feature.
 */
export class SortingStation {
  private readonly bins = new Map<BinId, THREE.Group>();
  private readonly pickables: THREE.Object3D[] = [];
  private readonly raycaster = new THREE.Raycaster();
  private readonly labelRenderer = new CSS2DRenderer();
  private readonly sparkle = new BurstParticles();
  private readonly textureLoader = new THREE.TextureLoader();
  private readonly textureCache = new Map<string, THREE.Texture>();
  private readonly itemSprite: THREE.Sprite;
  private readonly revealSprite: THREE.Sprite;
  private readonly labels: CSS2DObject[] = [];
  private readonly labelProbe = new THREE.Vector3();
  private layerWidth = 0;
  private layerHeight = 0;

  private itemState: ItemState = 'hidden';
  private bobPhase = Math.random() * Math.PI * 2;
  private flyElapsed = 0;
  private flyStart = new THREE.Vector3();
  private flyTarget = new THREE.Vector3();
  private revealElapsed = REVEAL_DURATION; // starts "already finished" so update() is a no-op
  private revealOrigin = new THREE.Vector3();

  constructor(
    private readonly scene: THREE.Scene,
    private readonly camera: THREE.PerspectiveCamera,
    labelParent: HTMLElement,
    cards: WasteCard[],
  ) {
    const positions = binRowPositions();
    BIN_ORDER.forEach((binId) => {
      const position = positions.get(binId)!;
      const bin = createBin3D(binId, position);
      this.bins.set(binId, bin);
      this.pickables.push(...bin3DPickables(bin));

      const labelDiv = document.createElement('div');
      labelDiv.className = 'bin-label';
      labelDiv.textContent = BINS[binId].labelKo;
      labelDiv.style.setProperty('--bin-color', BINS[binId].color);
      const label = new CSS2DObject(labelDiv);
      label.center.set(0.5, 1); // anchor at the label's bottom edge so it floats above the bin
      label.position.set(0, 0.95, 0);
      bin.add(label);
      this.labels.push(label);

      this.scene.add(bin);
    });

    this.sparkle.points.position.set(0, 0, 0);
    this.scene.add(this.sparkle.points);

    const material = new THREE.SpriteMaterial({ transparent: true });
    this.itemSprite = new THREE.Sprite(material);
    this.itemSprite.scale.set(0.9, 0.9, 1);
    this.itemSprite.visible = false;
    this.scene.add(this.itemSprite);

    this.revealSprite = new THREE.Sprite(new THREE.SpriteMaterial({ transparent: true }));
    this.revealSprite.visible = false;
    this.scene.add(this.revealSprite);

    const iconPaths = new Set(cards.map((card) => card.iconRef));
    for (const card of cards) {
      if (card.becomesIconRef) iconPaths.add(card.becomesIconRef);
    }
    for (const path of iconPaths) {
      this.textureLoader.load(path, (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        this.textureCache.set(path, texture);
      });
    }

    this.labelRenderer.domElement.style.position = 'absolute';
    this.labelRenderer.domElement.style.top = '0';
    this.labelRenderer.domElement.style.left = '0';
    this.labelRenderer.domElement.style.pointerEvents = 'none';
    // Insert right after the canvas (not appended last) so every UI panel — answer sheet,
    // summary, intro — still paints on top of these labels instead of the reverse.
    labelParent.insertBefore(this.labelRenderer.domElement, labelParent.children[1] ?? null);
  }

  /** Bins (and their CSS2D labels) only make sense while a card is actively being sorted —
   * otherwise the idle-orbiting camera drags them into view at odd angles behind other UI. */
  setVisible(visible: boolean): void {
    for (const bin of this.bins.values()) bin.visible = visible;
  }

  showItem(card: WasteCard): void {
    const texture = this.textureCache.get(card.iconRef);
    if (texture) {
      this.itemSprite.material.map = texture;
      this.itemSprite.material.needsUpdate = true;
    }
    this.itemSprite.position.copy(ITEM_REST_POSITION);
    this.itemSprite.visible = true;
    this.itemState = 'idle';
  }

  hideItem(): void {
    this.itemSprite.visible = false;
    this.itemState = 'hidden';
  }

  /** @returns the BinId whose 3D mesh was hit at these normalized device coordinates, if any. */
  pickBinAt(ndcX: number, ndcY: number): BinId | null {
    this.raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), this.camera);
    const hits = this.raycaster.intersectObjects(this.pickables, false);
    if (hits.length === 0) return null;
    const binId = hits[0].object.userData.binId;
    return typeof binId === 'string' ? (binId as BinId) : null;
  }

  flyItemTo(binId: BinId): void {
    const bin = this.bins.get(binId);
    if (!bin || !this.itemSprite.visible) return;
    this.flyStart.copy(this.itemSprite.position);
    this.flyTarget.copy(bin.position).add(new THREE.Vector3(0, 0.75, 0));
    this.flyElapsed = 0;
    this.itemState = 'flying';
  }

  /** Reuses the existing correct-answer sparkle burst as the "it became something new" spark. */
  triggerTransformSpark(binId: BinId): void {
    const bin = this.bins.get(binId);
    if (!bin) return;
    this.sparkle.trigger(bin.position.clone().add(new THREE.Vector3(0, 0.6, 0)), true);
  }

  /**
   * Pops up the "what it became" icon — a no-op if the card has no such icon yet.
   * Deliberately reuses the item's hover spot (not the bin) — that spot is verified clear of the
   * bin-label CSS2D layer, which otherwise paints over (hides) anything rising up near the bins:
   * CSS2DRenderer's DOM elements always draw on top of the WebGL canvas, with no real depth test.
   */
  showTransformResult(iconRef: string | undefined): void {
    if (!iconRef) return;
    const texture = this.textureCache.get(iconRef);
    if (!texture) return;

    this.revealSprite.material.map = texture;
    this.revealSprite.material.needsUpdate = true;
    this.revealSprite.material.opacity = 0;
    this.revealSprite.scale.set(0.001, 0.001, 1);
    this.revealOrigin.copy(ITEM_REST_POSITION);
    this.revealSprite.position.copy(this.revealOrigin);
    this.revealSprite.visible = true;
    this.revealElapsed = 0;
  }

  update(delta: number): void {
    if (this.itemState === 'idle') {
      this.bobPhase += delta * IDLE_BOB_SPEED;
      this.itemSprite.position.y = ITEM_REST_POSITION.y + Math.sin(this.bobPhase) * IDLE_BOB_AMOUNT;
    } else if (this.itemState === 'flying') {
      this.flyElapsed += delta;
      const t = Math.min(this.flyElapsed / FLY_DURATION, 1);
      this.itemSprite.position.lerpVectors(this.flyStart, this.flyTarget, t);
      this.itemSprite.scale.setScalar(0.9 * (1 - t * 0.7));
      if (t >= 1) {
        this.itemSprite.visible = false;
        this.itemSprite.scale.set(0.9, 0.9, 1);
        this.itemState = 'hidden';
      }
    }
    this.sparkle.update(delta);

    if (this.revealElapsed < REVEAL_DURATION) {
      this.revealElapsed += delta;
      const t = this.revealElapsed;
      let scale: number;
      let opacity: number;
      let riseY: number;

      if (t < REVEAL_RISE_END) {
        const p = t / REVEAL_RISE_END;
        // Slight overshoot bounce so the pop-in reads as a "ta-da!" rather than a plain fade.
        scale = REVEAL_PEAK_SCALE * (Math.sin(p * Math.PI * 0.5) * 1.1 - Math.sin(p * Math.PI) * 0.1);
        opacity = p;
        riseY = p * 0.2;
      } else if (t < REVEAL_HOLD_END) {
        scale = REVEAL_PEAK_SCALE;
        opacity = 1;
        riseY = 0.2 + Math.sin((t - REVEAL_RISE_END) * 3) * 0.03;
      } else {
        const p = (t - REVEAL_HOLD_END) / (REVEAL_DURATION - REVEAL_HOLD_END);
        scale = REVEAL_PEAK_SCALE * (1 - p * 0.2);
        opacity = 1 - p;
        riseY = 0.2 + p * 0.25;
      }

      this.revealSprite.scale.set(Math.max(scale, 0.001), Math.max(scale, 0.001), 1);
      this.revealSprite.material.opacity = Math.max(opacity, 0);
      this.revealSprite.position.set(this.revealOrigin.x, this.revealOrigin.y + riseY, this.revealOrigin.z);

      if (this.revealElapsed >= REVEAL_DURATION) {
        this.revealSprite.visible = false;
      }
    }
  }

  renderLabels(): void {
    this.layoutLabels();
    this.labelRenderer.render(this.scene, this.camera);
  }

  /**
   * Fit the label boxes to the projected bin spacing and pull the outermost ones back inside
   * the viewport. Runs per frame (six projections) rather than on resize because the camera
   * keeps orbiting, so the projected positions move without any resize event.
   */
  private layoutLabels(): void {
    const width = this.layerWidth;
    const height = this.layerHeight;
    if (width < 2 || height < 2 || this.labels.length < 2) return;

    const centersX: number[] = [];
    for (const label of this.labels) {
      label.getWorldPosition(this.labelProbe);
      this.labelProbe.project(this.camera);
      centersX.push((this.labelProbe.x * 0.5 + 0.5) * width);
    }

    let spacing = Infinity;
    for (let i = 1; i < centersX.length; i += 1) {
      spacing = Math.min(spacing, Math.abs(centersX[i] - centersX[i - 1]));
    }

    const labelWidth = clamp(spacing - LABEL_MIN_GAP_PX, LABEL_MIN_WIDTH_PX, LABEL_MAX_WIDTH_PX);
    const ratio = (labelWidth - LABEL_MIN_WIDTH_PX) / (LABEL_MAX_WIDTH_PX - LABEL_MIN_WIDTH_PX);
    const fontRem = LABEL_MIN_FONT_REM + ratio * (LABEL_MAX_FONT_REM - LABEL_MIN_FONT_REM);
    const layer = this.labelRenderer.domElement;
    layer.style.setProperty('--bin-label-w', `${labelWidth.toFixed(1)}px`);
    layer.style.setProperty('--bin-label-fs', `${fontRem.toFixed(3)}rem`);

    // CSS2DObject.center.x is the fraction of the element that sits left of the anchor, so
    // shrinking it slides the box right and growing it slides the box left.
    this.labels.forEach((label, index) => {
      const x = centersX[index];
      const lowest = 1 - (width - LABEL_EDGE_MARGIN_PX - x) / labelWidth;
      const highest = (x - LABEL_EDGE_MARGIN_PX) / labelWidth;
      label.center.x = lowest > highest ? 0.5 : clamp(0.5, lowest, highest);
    });
  }

  resize(width: number, height: number): void {
    this.layerWidth = width;
    this.layerHeight = height;
    this.labelRenderer.setSize(width, height);
  }

  dispose(): void {
    for (const bin of this.bins.values()) {
      disposeBin3D(bin);
      this.scene.remove(bin);
    }
    this.sparkle.dispose();
    this.itemSprite.material.dispose();
    this.revealSprite.material.dispose();
    for (const texture of this.textureCache.values()) texture.dispose();
    this.labelRenderer.domElement.remove();
  }
}
